<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { Notify, QForm, useDialogPluginComponent } from 'quasar';
import isEmpty from 'validator/lib/isEmpty';
import isLength from 'validator/lib/isLength';
import { type IDenialOption, DENIAL_OPTIONS, type DeniableFileType } from '@/components/ManagementAccountingFileStatus/types.ts';
import { useFileStatusChange } from '@/components/ManagementAccountingFileStatus/useAccountingFileStatusChange.ts';
import { FileAccStatus, FileRejectionReason } from '@/generated/graphql.ts';
import { FileEnumAccounting } from '@/helpers/types.ts';

const props = defineProps<{
  files: DeniableFileType[];
  source?: FileEnumAccounting
}>();

const { dialogRef: denyFileDialogRef, onDialogOK } = useDialogPluginComponent();
const { updateAccountingApprovalStatus } = useFileStatusChange();

const formRef = ref<InstanceType<typeof QForm> | null>(null);
const isDenying = ref(false);
const denialOptions = ref<IDenialOption[]>(DENIAL_OPTIONS);

const selectedReasonId = ref<FileRejectionReason>(denialOptions.value[0].id);
const selectedReason = ref<IDenialOption>(denialOptions.value[0]);
const customReason = ref('');

const isMultipleFiles = computed(() => props.files.length > 1);

const isEditing = computed(() => props.files.every(file => file.accStatusId === FileAccStatus.Denied));

const dialogTitle = computed(() =>
  isEditing.value ? 'Change denial reason' : 'Please provide the reason',
);

const isOtherSelected = computed(() => {
  return selectedReason.value.id === FileRejectionReason.Other;
});

const inputRules = computed(() => [
  (val: string) => !isOtherSelected.value || !isEmpty(val) || 'Please provide a reason',
  (val: string) => isEmpty(val) || isLength(val, { max: 200 }) || 'Comment cannot exceed 200 characters',
]);

const isTransferFile = computed(() => props.source === FileEnumAccounting.TransferFileType);

const handleSubmit = async () => {
  const isValid = await formRef.value?.validate();

  if (!isValid) return;

  isDenying.value = true;
  try {
    const fileIds = props.files.map(file => file.id);
    const success = await updateAccountingApprovalStatus({
      fileIds,
      accStatusId: FileAccStatus.Denied,
      rejectionReasonId: selectedReason.value.id,
      ...(customReason.value && { rejectionComment: customReason.value }),
    });

    if (success) {
      onDialogOK({
        fileId: fileIds[0],
        fileIds,
        accStatusId: FileAccStatus.Denied,
        rejectionReasonId: selectedReason.value.id,
        rejectionComment: customReason.value,
      });
    }
  } catch (err) {
    console.error(err);
    Notify.create({
      message: 'Failed to process file. Please try again.',
      color: 'negative',
      position: 'top',
    });
  } finally {
    isDenying.value = false;
  }
};

const initializeFormData = () => {
  const singleFile = !isMultipleFiles.value ? props.files[0] : null;

  if (isTransferFile.value) {
    const otherOption = denialOptions.value.find(option => option.id === FileRejectionReason.Other);
    if (otherOption) {
      selectedReasonId.value = otherOption.id;
      selectedReason.value = otherOption;
    }
  } else if (singleFile?.rejectionReasonId) {
    selectedReasonId.value = singleFile.rejectionReasonId;
    const savedReason = denialOptions.value.find(option => option.id === singleFile.rejectionReasonId);
    if (savedReason) {
      selectedReason.value = savedReason;
    }
  } else {
    selectedReasonId.value = denialOptions.value[0].id;
    selectedReason.value = denialOptions.value[0];
  }

  customReason.value = singleFile?.rejectionComment || '';
};

watch(() => props.files, (newFiles) => {
  if (newFiles?.length) {
    initializeFormData();
  }
}, { immediate: true });

watch(selectedReasonId, (newId) => {
  selectedReason.value = denialOptions.value.find(option => option.id === newId) || denialOptions.value[0];
});
</script>

<template>
  <q-dialog
    ref="denyFileDialogRef"
  >
    <q-card class="full-width q-dialog-deny-file">
      <q-card-section class="q-px-lg q-pt-lg">
        <div class="row items-center justify-between">
          <div class="text-h6">
            {{ dialogTitle }}
          </div>
          <q-btn
            v-close-popup
            flat
            round
            dense
            size="md"
            color="primary"
            icon="close"
          />
        </div>
        <div
          v-if="isMultipleFiles"
          class="text-caption text-grey q-mt-xs"
        >
          {{ files.length }} files selected
        </div>
      </q-card-section>

      <q-card-section class="q-pa-lg">
        <q-form
          ref="formRef"
          class="q-gutter-y-md column"
          @submit.prevent="handleSubmit"
        >
          <q-select
            v-model="selectedReasonId"
            outlined
            color="secondary"
            text-color="secondary"
            :options="denialOptions"
            stack-label
            option-value="id"
            option-label="title"
            emit-value
            map-options
            label="Reason"
            :readonly="isTransferFile"
          >
            <template #prepend>
              <q-icon
                name="warning"
                size="xs"
                color="secondary"
              />
            </template>
          </q-select>

          <q-input
            v-model.trim="customReason"
            type="textarea"
            outlined
            autogrow
            autofocus
            color="secondary"
            :label="isOtherSelected ? 'Comment' : 'Comment (optional)'"
            :rules="inputRules"
            lazy-rules
          />

          <q-card-actions
            align="right"
            class="text-primary q-pt-lg q-pb-lg"
          >
            <q-btn
              v-close-popup
              flat
              no-caps
              color="primary"
              label="Cancel"
            />
            <q-btn
              color="secondary"
              flat
              no-caps
              :ripple="{ center: true }"
              :label="isEditing ? 'Update' : 'Deny'"
              type="submit"
              :loading="isDenying"
            />
          </q-card-actions>
        </q-form>
      </q-card-section>
    </q-card>
  </q-dialog>
</template>

<style scoped lang="scss">
.q-dialog-deny-file {
  max-width: 470px;
}
</style>
